import { defineListView } from "@/base/list/defineListView";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  isUnread,
  listNotifications,
  notificationDestination,
  notificationLink,
  type InboxItem,
} from "@/lib/brokerNotifications";
import { formatShortDateTime } from "@/lib/listFormat";
import {
  NoticeActions,
  NoticeKindLabel,
  NoticeSubtitle,
} from "@/components/InboxRowParts";

// The broker's in-app inbox as a BaseListView definition (BRK-S252, over
// PMS-S353). Newest first, 25 a page, and paging only: PMS's inbox reads
// `limit` and `offset` and nothing else (`listInboxFor`), so a search or sort
// control would do nothing. Reading a page marks its rows SEEN server-side,
// which is what clears the bell's badge.
//
// A row links to its destination when it has one (`notificationDestination`,
// BRK-S177, BRK-S182), and says where in the subtitle; a notice about no record
// is text. Every row says what KIND of notice it is in its own Type column
// (BRK-S188): the mirror's label and icon, or a neutral "Update". Mark read and Archive act on PMS, then re-read the page, so a row
// always shows what PMS now holds, never what the click hoped for.

export function notificationsListView({
  request,
}: {
  request: AuthedRequester;
}) {
  return defineListView<InboxItem>({
    endpoint: "/api/broker/notifications",
    noun: "notifications",
    title: "Notifications",
    context: "Updates about your submissions, bonds, and account.",
    columns: [
      {
        id: "notice",
        header: "Notification",
        kind: "title",
        // Unread reads bold, and a read notice doesn't, title or not.
        cell: (n) => (
          <span className={isUnread(n) ? "font-semibold" : "font-normal"}>
            {n.subject}
          </span>
        ),
        subtitle: (n) => <NoticeSubtitle item={n} />,
      },
      {
        id: "type",
        header: "Type",
        cell: (n) => <NoticeKindLabel item={n} />,
      },
      {
        id: "received",
        header: "Received",
        cell: (n) => formatShortDateTime(n.createdAt),
      },
    ],
    rowKey: (n) => n.id,
    rowHref: (n) => notificationLink(n),
    rowLabel: (n) => {
      const destination = notificationDestination(n);
      return destination ? `${n.subject}: ${destination.label}` : n.subject;
    },
    rowActions: (n, list) => (
      <NoticeActions item={n} request={request} list={list} />
    ),
    pageSize: 25,
    empty: {
      title: "You’re all caught up",
      body: "Updates about your submissions, bonds, and account appear here.",
    },
    load: ({ limit, offset }) => listNotifications(request, { limit, offset }),
  });
}
